import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  AimOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BranchesOutlined,
  CameraOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  FileTextOutlined,
  FlagOutlined,
  ForkOutlined,
  GlobalOutlined,
  PartitionOutlined,
  PlayCircleOutlined,
  RetweetOutlined,
  RightOutlined,
  SafetyOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { DataTable, TabsTruot } from '../../../../components/shared';
import { Button, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { LOAI_LUONG, TONG_BUOC } from '../../constants';
import { useSoDo } from '../../hooks/useOpenCore';
import { BO_LOC_MAC_DINH } from '../../nghiep-vu/gia-tri';
import type { BanNhapGiaTri, BoLocGiaTri } from '../../nghiep-vu/gia-tri';
import { cacBuoc, loaiCua, workflowCon } from '../../nghiep-vu/workflow';
import type { BuocSoDo, LoaiBuoc, SoDoWorkflow, ThuocTinhBuoc } from '../../types';
import { BadgeLoaiLuong, LoiRpa } from '../chung';
import { NutChay } from './NutChay';
import { NutKetNoiOpenCore } from './NutKetNoiOpenCore';
import { TabGiaTri } from './TabGiaTri';

export type TabWorkflow = 'props' | 'values' | 'info';

const ICON_LOAI: Record<LoaiBuoc, ReactNode> = {
  start: <PlayCircleOutlined />,
  end: <FlagOutlined />,
  activity: <AppstoreOutlined />,
  decision: <BranchesOutlined />,
  loop: <RetweetOutlined />,
  try: <SafetyOutlined />,
  parallel: <ForkOutlined />,
  invoke: <ApiOutlined />,
};

/** Biểu tượng theo tên activity OpenRPA — chỉ để nhận diện nhanh, không đổi nghĩa ô (như native). */
const ICON_THEO_TEN: [RegExp, ReactNode][] = [
  [/url|browser|navigate/i, <GlobalOutlined key="web" />],
  [/type|text|set/i, <EditOutlined key="type" />],
  [/click|mouse/i, <AimOutlined key="click" />],
  [/getelement|find|search/i, <SearchOutlined key="find" />],
  [/download|save|write|file/i, <DownloadOutlined key="file" />],
  [/screenshot|capture|image/i, <CameraOutlined key="shot" />],
  [/read|log|line/i, <FileTextOutlined key="log" />],
];

function iconCua(b: BuocSoDo): ReactNode {
  if (b.kind !== 'activity') return ICON_LOAI[b.kind];
  return ICON_THEO_TEN.find(([re]) => re.test(b.type))?.[1] ?? ICON_LOAI.activity;
}

/**
 * Vùng phải của Danh sách luồng (`WorkflowDiagramPanel` của native): thẻ tóm tắt kèm danh sách bước, rồi ba tab Thuộc
 * tính bước / Biến & tham số / Thông tin. Dựng lại theo `key` khi đổi Workflow, nên bản nháp giá trị không bao giờ
 * sang nhầm Workflow khác.
 *
 * TODO(ADR-0103): sơ đồ đầy đủ (ô và mũi tên, `edges`) — native dùng `@xyflow/react` + `@dagrejs/dagre`, kho cấm thêm
 * thư viện giao diện; đợt sau vẽ bằng SVG tự dựng từ `nodes`/`edges` máy chủ đã trả.
 */
export function PanelWorkflow({
  workflowId,
  tabMoSan,
  suaDuoc,
  chayDuoc,
  giaiCon,
  onMoCon,
}: {
  workflowId: string;
  tabMoSan: TabWorkflow;
  suaDuoc: boolean;
  chayDuoc: boolean;
  /** Id của Workflow con mà `target` trỏ tới, nếu tìm được. */
  giaiCon: (target: string) => string | undefined;
  onMoCon: (id: string) => void;
}) {
  const { t, locale } = useT();
  const q = useSoDo(workflowId);
  const [buoc, setBuoc] = useState<BuocSoDo | null>(null);
  const [tab, setTab] = useState<TabWorkflow>(tabMoSan);
  const [lanDung, setLanDung] = useState(0);
  const [banNhap, setBanNhap] = useState<BanNhapGiaTri>({});
  const [loc, setLoc] = useState<BoLocGiaTri>(BO_LOC_MAC_DINH);

  if (!workflowId) {
    return <Empty icon={PartitionOutlined} title={t('rpaWorkflow.chonWorkflow')} description={t('rpaWorkflow.chonWorkflowMoTa')} className="py-24" />;
  }
  if (q.isPending) return <Skeleton className="h-96 w-full" />;
  if (q.isError) return <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />;

  const sd = q.data;
  const chonBuoc = (b: BuocSoDo) => {
    setBuoc(b);
    if (tab !== 'props') {
      setTab('props');
      // `TabsTruot` chỉ đọc tab mở sẵn lúc dựng: dựng lại hàng tab để chuyển sang Thuộc tính bước.
      setLanDung((n) => n + 1);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="min-w-0 break-words text-base font-semibold text-content-primary">{sd.name}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-content-muted">{t('rpaWorkflow.loaiLuong')}</span>
          <BadgeLoaiLuong loai={loaiCua(sd)} />
          {chayDuoc && <NutKetNoiOpenCore />}
          {chayDuoc && <NutChay workflowId={sd.id} ten={sd.name} />}
        </div>
      </div>

      {sd.truncated && (
        <p role="status" className="flex items-center gap-1.5 text-xs text-warning-700">
          <WarningOutlined aria-hidden />
          {t('rpaWorkflow.catBot', { so: soVN(sd.nodes.length, locale) })}
        </p>
      )}

      <TheTomTat soDo={sd} dangChon={buoc?.id ?? ''} onChon={chonBuoc} giaiCon={giaiCon} onMoCon={onMoCon} />

      <TabsTruot
        key={lanDung}
        gon
        xuongDong
        defaultActiveKey={tab}
        onChange={(k) => setTab(k as TabWorkflow)}
        items={[
          { key: 'props', label: t('rpaWorkflow.tabThuocTinh'), children: <ThuocTinhBuoc buoc={buoc} /> },
          {
            key: 'values',
            label: t('rpaWorkflow.tabGiaTri'),
            children: (
              <TabGiaTri
                workflowId={sd.id}
                ten={sd.name}
                suaDuoc={suaDuoc}
                banNhap={banNhap}
                setBanNhap={setBanNhap}
                loc={loc}
                setLoc={setLoc}
              />
            ),
          },
          { key: 'info', label: t('rpaWorkflow.tabThongTin'), children: <ThongTin soDo={sd} /> },
        ]}
      />
    </div>
  );
}

/** Thẻ tóm tắt (`SummaryNode` của native): tên, số bước, bấm để mở/gập danh sách bước đánh số. */
function TheTomTat({
  soDo,
  dangChon,
  onChon,
  giaiCon,
  onMoCon,
}: {
  soDo: SoDoWorkflow;
  dangChon: string;
  onChon: (b: BuocSoDo) => void;
  giaiCon: (target: string) => string | undefined;
  onMoCon: (id: string) => void;
}) {
  const { t, locale } = useT();
  const [mo, setMo] = useState(true);
  const l = LOAI_LUONG[loaiCua(soDo)];
  const buoc = cacBuoc(soDo.nodes);

  return (
    <section className={cn('min-w-0 overflow-hidden rounded-lg border-2 bg-surface', l.vien)}>
      <button
        type="button"
        aria-expanded={mo}
        onClick={() => setMo((v) => !v)}
        className={cn('flex w-full items-center gap-3 px-4 py-3 text-left', l.nen)}
      >
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded text-xl', l.chu)}>
          <BranchesOutlined aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-content-primary" title={soDo.name}>{soDo.name}</span>
          <span className="block text-xs text-content-muted">{t('rpaWorkflow.soBuoc', { so: soVN(buoc.length, locale) })}</span>
        </span>
        <span className="sr-only">{t(mo ? 'rpaWorkflow.anBuoc' : 'rpaWorkflow.hienBuoc')}</span>
        <span className={l.chu} aria-hidden>{mo ? <DownOutlined /> : <RightOutlined />}</span>
      </button>
      {mo &&
        (buoc.length === 0 ? (
          <p className="px-4 py-3 text-sm text-content-muted">{t('rpaWorkflow.chuaCoBuoc')}</p>
        ) : (
          <ol className="max-h-80 space-y-0.5 overflow-y-auto overscroll-contain p-2" aria-label={t('rpaWorkflow.danhSachBuoc', { ten: soDo.name })}>
            {buoc.map((b, i) => {
              const conId = b.target ? giaiCon(b.target) : undefined;
              const chon = b.id === dangChon;
              return (
                <li key={b.id} className={cn('flex min-w-0 items-center gap-1 rounded', chon && cn(l.nen, 'ring-1 ring-inset', l.vong))}>
                  <button
                    type="button"
                    aria-pressed={chon}
                    onClick={() => onChon(b)}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-2 text-left hover:bg-subtle"
                  >
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-strong text-2xs tabular-nums text-content-muted">
                      {i + 1}
                    </span>
                    <span className={cn('flex size-6 shrink-0 items-center justify-center rounded', TONG_BUOC[b.kind])} aria-hidden>
                      {iconCua(b)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-content-primary" title={b.label}>{b.label}</span>
                      {b.type && <span className="block truncate text-xs text-content-muted">{b.type}</span>}
                    </span>
                  </button>
                  {conId && (
                    <Button variant="link" size="sm" icon={<ExportOutlined />} onClick={() => onMoCon(conId)}>
                      {t('rpaWorkflow.moWorkflowCon')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
        ))}
    </section>
  );
}

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="break-all text-sm text-content-primary">{children || KHONG_CO}</dd>
    </div>
  );
}

function ThuocTinhBuoc({ buoc }: { buoc: BuocSoDo | null }) {
  const { t } = useT();
  if (!buoc) return <Empty title={t('rpaWorkflow.chonBuoc')} className="py-6" />;
  return (
    <div className="min-w-0 space-y-3">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Dong nhan={t('rpaWorkflow.tenBuoc')}>{buoc.label}</Dong>
        <Dong nhan={t('rpaWorkflow.loaiActivity')}>{buoc.type}</Dong>
        <Dong nhan={t('rpaWorkflow.maBuoc')}>{buoc.idRef}</Dong>
        {buoc.target && <Dong nhan={t('rpaWorkflow.workflowCon')}>{buoc.target}</Dong>}
      </dl>
      {buoc.props.length === 0 ? (
        <p className="text-sm text-content-muted">{t('rpaWorkflow.khongThuocTinh')}</p>
      ) : (
        <DataTable<ThuocTinhBuoc>
          caption={t('rpaWorkflow.captionThuocTinh', { ten: buoc.label })}
          size="small"
          rowKey="name"
          pagination={false}
          dataSource={buoc.props}
          columns={[
            { title: t('rpaWorkflow.cotThuocTinh'), key: 'name', width: 180, render: (_, p) => <span className="break-all">{p.name}</span> },
            { title: t('rpaWorkflow.cotGiaTri'), key: 'value', render: (_, p) => <code className="break-all text-xs">{p.value}</code> },
          ]}
        />
      )}
    </div>
  );
}

function ThongTin({ soDo }: { soDo: SoDoWorkflow }) {
  const { t, locale } = useT();
  const con = workflowCon(soDo.nodes);
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <Dong nhan={t('rpaWorkflow.ttTen')}>{soDo.name}</Dong>
      <Dong nhan={t('rpaWorkflow.ttProject')}>{soDo.projectName}</Dong>
      <Dong nhan={t('rpaWorkflow.ttCapNhat')}>{soDo.updatedAt ? ngayGioVN(soDo.updatedAt, locale) : ''}</Dong>
      <Dong nhan={t('rpaWorkflow.ttId')}>{soDo.id}</Dong>
      <Dong nhan={t('rpaWorkflow.loaiLuong')}>{t(LOAI_LUONG[loaiCua(soDo)].khoa)}</Dong>
      <Dong nhan={t('rpaWorkflow.ttNguoiCapNhat')}>{soDo.updatedBy}</Dong>
      <Dong nhan={t('rpaWorkflow.ttMoTa')}>{soDo.description}</Dong>
      <Dong nhan={t('rpaWorkflow.ttSoBuoc')}>{soVN(cacBuoc(soDo.nodes).length, locale)}</Dong>
      <Dong nhan={t('rpaWorkflow.ttCon')}>{con.join(', ')}</Dong>
    </dl>
  );
}
